import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
    AuctionBuilderBidErrorCode,
    AuctionCampaign,
    AuctionDataField,
    auctionSelector,
    auctionSelectorNonNullable,
    BiddingStrategy,
    clearAuctionErrors,
    setErrors,
} from 'store/models/auctionData';
import { useSelector } from 'store/utils';

type ValidateBidFn = (bidValue: number | null, budgetValue?: number | null) => boolean;

export const getBidValidationError = (
    bidValue: number | null,
    budgetValue: number | null,
    minValue: number,
    auctionCampaign: AuctionCampaign | null,
    biddingStrategy: BiddingStrategy
): AuctionBuilderBidErrorCode | false => {
    if (bidValue === null) {
        return AuctionBuilderBidErrorCode.ValueMissing;
    }
    if (bidValue < minValue) {
        return biddingStrategy === BiddingStrategy.Auto
            ? AuctionBuilderBidErrorCode.AutoStrategyRangeUnderflow
            : AuctionBuilderBidErrorCode.RangeUnderflow;
    }
    if (budgetValue !== null && bidValue > budgetValue) {
        return AuctionBuilderBidErrorCode.RangeOverflow;
    }
    if (
        auctionCampaign &&
        budgetValue !== null &&
        bidValue > budgetValue - (auctionCampaign.budget - auctionCampaign.campaignBalance)
    ) {
        return AuctionBuilderBidErrorCode.BidCanNotBeMoreThanCampaignBalance;
    }
    return false;
};

export const useAuctionBidValidation = (): ValidateBidFn => {
    const minValue = useSelector(auctionSelectorNonNullable.bid).minValue;
    const storedBudgetValue = useSelector(auctionSelectorNonNullable.budget).value;
    const auctionCampaign = useSelector(auctionSelector.auctionCampaign);
    const biddingStrategy = useSelector(auctionSelectorNonNullable.biddingStrategy);

    const dispatch = useDispatch();

    return useCallback<ValidateBidFn>(
        (bid: number | null, budgetValue?: number | null) => {
            const budget = budgetValue ?? storedBudgetValue;
            const errorCode = getBidValidationError(bid, budget, minValue, auctionCampaign, biddingStrategy);

            if (errorCode) {
                dispatch(setErrors({ bid: errorCode }));
                return false;
            }
            dispatch(clearAuctionErrors(AuctionDataField.Bid));
            return true;
        },
        [auctionCampaign, biddingStrategy, dispatch, minValue, storedBudgetValue]
    );
};
